/**
 * A wrapper class managing the syncing of multiple viewports.
 */
export class ViewportSyncronizer {
  constructor (uScope, trackerFactory) {
    this._uScope = uScope;
    this._factory = trackerFactory;
    this._trackers = [];
    this._savedStates = {};
  }

  /**
     * Refreshes the list of viewports and indicates which one is currently selected.
     */
  refresh () {
    const selectedViewport = this._uScope.getThisImage();
    const allViewports = this._uScope.getAllImages();

    // allViewports is sometimes undefined.
    // Possibly it is called 2 times _handleSelectedViewportChange and/or _handleViewportAdded?
    if (!allViewports) {
      throw new Error('there are no viewports - why are we here?');
    }

    // Add any missing viewports. Update selected flag while we're in here.
    allViewports.forEach((viewport) => {
      if (viewport === undefined) {
        return;
      }

      const tracker = this._findOrCreateTracker(viewport);

      tracker.isSelected = (viewport === selectedViewport);
    });

    // Remove viewports that are no longer open.
    this._removeOrphanedTrackers(allViewports);
  }

  _findOrCreateTracker (viewport) {
    // Look for the viewport in the list of trackers and return it if we find it.
    for (let tracker of this._trackers) {
      if (tracker.viewport === viewport) {
        return tracker;
      }
    }

    // If it's not found, create it.
    return this._createViewportTracker(viewport);
  }

  _createViewportTracker (viewport) {
    const tracker = this._factory.create(viewport);
    this._trackers.push(tracker);
    return tracker;
  }

  _removeOrphanedTrackers (allViewports) {
    if (this._trackers.length === 0) {
      return;
    }

    this._trackers = this._trackers.filter((tracker) => {
      for (let viewport of allViewports) {
        if (viewport && tracker.viewport === viewport) {
          return true;
        }
      }
      return false;
    });
  }

  /**
     * Instructs each viewport tracker to begin tracking.
     */
  start () {
    this._trackers.forEach(vp => vp.start());
  }

  /**
     * Instructs each viewport tracker to end tracking.
     */
  stop () {
    this._trackers.forEach(vp => vp.stop());
  }

  /**
     * Instructs each tracker to synchronize its viewport with the indicated state changes.
     * @param deltaState
     */
  sync (deltaState) {
    // note: switched to "forEach" rather than "for ...of" because the babel translation makes it harder to debug
    this._trackers.forEach((tracker) => {
      tracker.updateState(deltaState);
    });
  }

  /**
     * Indicates whether or not we can synchronize.
     * @returns {boolean}
     */
  get canSync () {
    return this._trackers.length > 1;
  }

  /**
     * Solely here to handle the case when the unpinned image is closed. Exposes the tracker's initial state
     * so that it can be restored. See the description for _isClosingUnpinnedImage in ManualSyncService for more info.
     */
  saveState () {
    this._trackers.forEach((tracker) => {
      const imageId = tracker.viewport.getImageId();
      this._savedStates[imageId] = tracker.getState();
    });
  }

  /**
     * Solely here to handle the case when the unpinned image is closed. Restores the tracker's initial state.
     * See the description for _isClosingUnpinnedImage in ManualSyncService for more info.
     */
  restoreState () {
    this._trackers.forEach((tracker) => {
      const imageId = tracker.viewport.getImageId();
      const state = this._savedStates[imageId];
      tracker.setState(state);
    });
    this._savedStates = {};
  }
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/manual-sync/ViewportSyncronizer.js